import { defineStore } from 'pinia'
import { useLocale } from '@/shared/composables/useLocale'
import {
  CONTACTS_KEYS,
  EnterpriseContact,
  EntryValuesType,
  CountriesType,
  EmailDomain,
  MaritalStatus,
} from '@/shared/types/other.types'
import { A_B_KEY, FLOW_REFERRER_KEY } from '@/shared/constants/other.constants'
import { useApiNew } from '@/shared/composables/useApi'
import { useFeaturesStore } from '@/shared/store/feature'

const { locale } = useLocale()

type RelationsType = {
  code: string;
  desc: string;
}

interface CoreState {
  nationalities: CountriesType[]
  countries: CountriesType[]
  relations: RelationsType[]
  durations: EntryValuesType[]
  contacts: EnterpriseContact[]
  emailDomains: EmailDomain[]
  maritalStatuses: MaritalStatus[]
  initialReferrer: string | null
  abHash: Record<string, string>
  isStopBack: boolean
  backCallback: (() => void) | null
}

const getDefaultAbHash = () => {
  let hash: Record<string, string> = {}

  const existingState = localStorage.getItem(A_B_KEY)
  if (existingState) {
    try {
      hash = JSON.parse(existingState)
    } catch {
      hash = {}
    }
  }

  return hash
}

export const useCoreStore = defineStore('core', {
  state: (): CoreState => ({
    nationalities: [],
    countries: [],
    relations: [],
    durations: [],
    contacts: [],
    emailDomains: [],
    maritalStatuses: [],
    initialReferrer: sessionStorage.getItem(FLOW_REFERRER_KEY),
    abHash: getDefaultAbHash(),
    isStopBack: false,
    backCallback: null,
  }),
  getters: {
    nationalitiesMap: state =>
      new Map(state.nationalities.map(nationality => [nationality.code, nationality])),
    countriesMap: state => new Map(state.countries.map(country => [country.code, country])),
    relationsMap: state => new Map(state.relations.map(relation => [relation.code, relation])),
    getContactValue: state => (key: CONTACTS_KEYS): string => {
      const contact = state.contacts.find(el => el.ContactKey === key)
        if (!contact) {
          return ''
        }

        return contact.Value
      },
    getInitialReferrer: state => state.initialReferrer || 'visitors',
    getAbValue: state => (code: string): string => state.abHash[code] || 'default',
    getMaritalStatusName: state => (code: string): string => 
      state.maritalStatuses.find(item => item.code === code)?.name || '',
  },
  actions: {
    async loadStoreData() {
      if (this.nationalities.length > 0 && this.countries.length > 0 && this.relations.length > 0 ) {
        return
      }

      const api = useApiNew()
      const { isMaritalStatusEnabled } = useFeaturesStore()
      const [nationalities, relations, maritalStatuses] = await Promise.all([
        api.commonSeervice?.getNationalities(locale),
        api.commonSeervice?.getRelations(locale),
        isMaritalStatusEnabled ? api.commonSeervice?.getMaritalStatuses(locale) : Promise.resolve([]),
      ])
      const filteredCountries = nationalities?.filter(item => !['990', '992'].includes(item.code))
      const sortedCountries = filteredCountries
        .sort((a: CountriesType, b: CountriesType) => {
        if (a.popularCountry && b.popularCountry) {
          return a.popularCountry - b.popularCountry
        }
        if (a.popularCountry) return -1
        if (b.popularCountry) return 1
        return a.desc.localeCompare(b.desc)
      })

      this.nationalities = nationalities || []
      this.countries = sortedCountries || []
      this.relations = relations || []
      this.maritalStatuses = maritalStatuses || []
    },
    async getPaymentOptions(quoteno: string) {
      const api = useApiNew()
      const options = await api.quoteService?.getPaymentOptions(quoteno)
      return options?.map((option) => option.paymtType) || []
    },
    async getDurations() {
      const api = useApiNew()
      const durations = await api.commonSeervice?.getVisitorsEntryType(locale)
      if (durations) {
        this.durations = durations
      }
    },
    async getAbTest(code: string) {
      if (this.abHash[code]) return undefined

      let newType = ''
      const api = useApiNew()
      try {
        const list = await api.commonSeervice?.getAbDistribution<string>(code)
        newType = list?.find(item => item.experiment === code)?.group || code
      } catch (error) {
        newType = 'default'
      } finally {
        this.abHash[code] = newType
        localStorage.setItem(A_B_KEY, JSON.stringify(this.abHash))
      }

      return newType
    },
    async getContacts() {
      if (this.contacts.length) {
        return this.contacts
      }
      const api = useApiNew()
      return api.commonSeervice?.getContacts()
        .then(data => {
        this.contacts = data
        return data
      })
    },
    setInitialReferrer(value: string) {
      this.initialReferrer = value
      sessionStorage.setItem(FLOW_REFERRER_KEY, value)
    },
    setStopBack(isStopBack: boolean): void {
      this.isStopBack = isStopBack
    },
    setBackCallback(backCallback: (() => void) | null): void {
      this.backCallback = backCallback
    },
    async getEmailDomains() {
      if (this.emailDomains.length) {
        return this.emailDomains
      }
      const api = useApiNew()
      try {
        const data = await api.commonSeervice?.getEmailDomains()
        this.emailDomains = data || []
        return this.emailDomains
      } catch (error) {
        console.error(error)
        return []
      }
    },
  },
})

// const cacheService = useCacheService()
// export const unsubscribeCache = store.subscribe((mutation, state) => {
//   if (!mutation.type.startsWith('visitors/')) return
//   cacheService.set(STATE_CACHE_KEY, state.visitors)
// })
